import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { useAsyncStatus } from '@peanuds/composables';
import type {
  InitiatePhoneChallengeData,
  InitiatePhoneChallengeResponseDtoOutput,
  VerifyPhoneChallengeData,
  VerifyPhoneChallengeResponseDtoOutput,
} from '@peanuds/shared';
import { peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';

import { useActiveIdentity } from '../auth0/composables/useActiveIdentity';
import { useAuthSessionStore } from '../auth0/store/auth-session.store';
import type { PhoneState } from './access-control.types';

const toPhoneSourceData = ({
  isVerified,
  phoneNumber,
  challengeId,
  challengeStatus,
}: {
  isVerified: boolean;
  phoneNumber: string | null;
  challengeId: string | null;
  challengeStatus: string | null;
}): PhoneState['data'] => ({
  isVerified,
  phoneNumber,
  challengeId,
  challengeStatus,
});

export const usePhoneSourceStore = defineStore('phone-source', () => {
  const authSessionStore = useAuthSessionStore();
  const activeIdentity = useActiveIdentity();
  const requestStatus = useAsyncStatus();

  const challengeId = ref<string | null>(null);
  const challengeStatus = ref<string | null>(null);
  const challengeErrorCode = ref<string | null>(null);
  const pendingPhoneNumber = ref<string | null>(null);
  const verifiedPhoneNumber = ref<string | null>(null);

  const resetPhoneState = () => {
    challengeId.value = null;
    challengeStatus.value = null;
    challengeErrorCode.value = null;
    pendingPhoneNumber.value = null;
    verifiedPhoneNumber.value = null;
    requestStatus.toIdle();
  };

  const phone = computed<PhoneState>(() => {
    if (!authSessionStore.jwtToken) {
      return {
        data: null,
        status: 'idle',
      };
    }

    const sourceStatus = requestStatus.isLoading.value
      ? 'loading'
      : requestStatus.isFailed.value
        ? 'failed'
        : 'loaded';

    return {
      data: toPhoneSourceData({
        isVerified: activeIdentity.isPhoneVerified.value || verifiedPhoneNumber.value !== null,
        phoneNumber:
          activeIdentity.phoneNumber.value ?? verifiedPhoneNumber.value ?? pendingPhoneNumber.value,
        challengeId: challengeId.value,
        challengeStatus: challengeStatus.value,
      }),
      status: sourceStatus,
    };
  });

  const isPhoneLoading = computed(() => requestStatus.isLoading.value);
  const isPhoneVerified = computed(() => {
    return phone.value.data?.isVerified ?? false;
  });

  const ensurePhoneResolved = async () => {
    if (!authSessionStore.jwtToken) {
      return;
    }

    if (!requestStatus.isLoading.value && !requestStatus.isFailed.value) {
      requestStatus.toLoaded();
    }
  };

  const initiatePhoneChallenge = async (phoneNumber: string) => {
    requestStatus.toLoading();
    challengeErrorCode.value = null;

    try {
      const response = await peanudsApi.initiatePhoneChallenge(
        await withAuthenticatedHeaders<InitiatePhoneChallengeData>({
          body: { phoneNumber },
        }),
      );

      if ('error' in response && response.error !== undefined) {
        challengeErrorCode.value = response.error.error?.code ?? null;
        requestStatus.toFailed();
        console.error('Failed to initiate phone challenge:', response.error);
        return null;
      }

      const responseData = (response.data?.data ?? null) as
        InitiatePhoneChallengeResponseDtoOutput['data'] | null;

      challengeId.value = responseData?.challengeId ?? null;
      challengeStatus.value = responseData?.status?.trim() ?? null;
      pendingPhoneNumber.value = responseData?.phoneNumber ?? phoneNumber;
      requestStatus.toLoaded();

      return responseData;
    } catch (error) {
      requestStatus.toFailed();
      console.error('Failed to initiate phone challenge:', error);
      return null;
    }
  };

  const verifyPhoneChallenge = async (code: string) => {
    const currentChallengeId = challengeId.value;

    if (!currentChallengeId) {
      console.error('Missing phone challenge ID for verification');
      return null;
    }

    requestStatus.toLoading();
    challengeErrorCode.value = null;

    try {
      const response = await peanudsApi.verifyPhoneChallenge(
        await withAuthenticatedHeaders<VerifyPhoneChallengeData>({
          body: {
            challengeId: currentChallengeId,
            code,
          },
        }),
      );

      if ('error' in response && response.error !== undefined) {
        challengeErrorCode.value = response.error.error?.code ?? null;
        requestStatus.toFailed();
        console.error('Failed to verify phone challenge:', response.error);
        return null;
      }

      const responseData = (response.data?.data ?? null) as
        VerifyPhoneChallengeResponseDtoOutput['data'] | null;

      const nextChallengeStatus = responseData?.status?.trim() ?? null;

      if (!responseData || nextChallengeStatus !== 'approved') {
        requestStatus.toFailed();
        return null;
      }

      challengeStatus.value = nextChallengeStatus;
      challengeId.value = null;
      pendingPhoneNumber.value = responseData?.phoneNumber ?? pendingPhoneNumber.value;
      verifiedPhoneNumber.value = responseData?.phoneNumber ?? pendingPhoneNumber.value;
      requestStatus.toLoaded();

      // Successful verification updates the Auth0 profile. The socket AUTH_JWT event is
      // responsible for reloading those claims through the shared auth refresh flow.

      return responseData;
    } catch (error) {
      requestStatus.toFailed();
      console.error('Failed to verify phone challenge:', error);
      return null;
    }
  };

  return {
    phone,
    isPhoneLoading,
    isPhoneVerified,
    challengeErrorCode,
    resetPhoneState,
    ensurePhoneResolved,
    initiatePhoneChallenge,
    verifyPhoneChallenge,
  };
});
